import type {
  ApiResponse,
  AuthUrlResponse,
  GoalSuggestionResponse,
  HealthData,
  IntervalsTokenData,
  LikelihoodUpdateResponse,
  TrainingGoal,
  TrainingPlan,
  UserProfile,
  Workout,
  WorkoutStep,
} from '@shared/types';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from '../utils/logger';
import type { AgentSessionChange, AICoachApplyResponse, AICoachChatResponse } from './agentService';

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
}

class ApiClient {
  private instance: AxiosInstance;
  private getAccessToken: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;
  private initializationPromise: Promise<void> | null = null;
  private resolveInitialization: (() => void) | null = null;

  constructor(config: ApiConfig) {
    // Create a promise that will resolve when setAccessTokenGetter is called
    this.initializationPromise = new Promise(resolve => {
      this.resolveInitialization = resolve;
    });
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for adding auth token
    this.instance.interceptors.request.use(
      async (config: any) => {
        // Wait for API client to be initialized with token getter
        if (this.initializationPromise) {
          await this.initializationPromise;
        }

        // Add Entra ID token to requests if available
        if (this.getAccessToken) {
          try {
            const token = await this.getAccessToken();
            if (token) {
              config.headers.Authorization = `Bearer ${token}`;
            } else {
              logger.warn('No token available for request');
            }
          } catch (error) {
            logger.error('Failed to get access token', error);
          }
        }

        return config;
      },
      (error: any) => {
        logger.error('API Request Error', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling and token refresh
    this.instance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        // Don't log 404 errors for clothing/sets/match endpoint (no match is expected)
        const isClothingMatchNotFound =
          error.response?.status === 404 && error.config?.url?.includes('/clothing/sets/match');

        if (!isClothingMatchNotFound) {
          logger.error('API Response Error', error);
        }

        const originalRequest = error.config;

        // Handle 401 errors with automatic token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Try to refresh the token
          if (this.getAccessToken) {
            try {
              logger.info('Token expired (401), attempting to refresh with forceRefresh=true');
              // Force refresh to get a new token (not from cache)
              const newToken = await this.getAccessToken(true);

              if (newToken) {
                logger.info('Token refreshed successfully, retrying original request');
                // Update both the instance default headers AND the request headers
                this.instance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                // Retry the original request with the new token
                return this.instance(originalRequest);
              } else {
                logger.error('Token refresh failed: No token returned');
                // Trigger auth error which will redirect to login
                window.dispatchEvent(
                  new CustomEvent('auth-error', {
                    detail: { reason: 'token_refresh_failed', redirectToLogin: true },
                  })
                );
              }
            } catch (refreshError) {
              logger.error('Token refresh failed with error', refreshError);
              // Trigger auth error which will redirect to login
              window.dispatchEvent(
                new CustomEvent('auth-error', {
                  detail: { reason: 'token_refresh_error', redirectToLogin: true },
                })
              );
              return Promise.reject(refreshError);
            }
          } else {
            // No token getter available
            logger.error('No token getter available for refresh');
            window.dispatchEvent(
              new CustomEvent('auth-error', {
                detail: { reason: 'no_token_getter', redirectToLogin: true },
              })
            );
          }
        }

        // Handle different types of errors
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          // Backend server is down or unreachable
          window.dispatchEvent(
            new CustomEvent('backend-connection-error', {
              detail: {
                type: 'CONNECTION_REFUSED',
                message: 'Backend server is not reachable. Please check if the server is running.',
                originalError: error,
              },
            })
          );
        } else if (error.code === 'ENOTFOUND') {
          // DNS resolution failed
          window.dispatchEvent(
            new CustomEvent('backend-connection-error', {
              detail: {
                type: 'DNS_ERROR',
                message:
                  'Cannot resolve backend server address. Please check your network connection.',
                originalError: error,
              },
            })
          );
        } else if (error.code === 'ECONNABORTED') {
          // Request timeout
          window.dispatchEvent(
            new CustomEvent('backend-connection-error', {
              detail: {
                type: 'TIMEOUT',
                message: 'Request to backend server timed out. The server may be overloaded.',
                originalError: error,
              },
            })
          );
        } else if (error.response?.status >= 500) {
          // Server errors
          window.dispatchEvent(
            new CustomEvent('backend-connection-error', {
              detail: {
                type: 'SERVER_ERROR',
                message: `Backend server error (${error.response.status}). Please try again later.`,
                originalError: error,
              },
            })
          );
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Set the function to get access tokens for API requests
   * This should be called during app initialization with the Entra auth function
   */
  public setAccessTokenGetter(getter: (forceRefresh?: boolean) => Promise<string | null>): void {
    this.getAccessToken = getter;
    // Resolve the initialization promise
    if (this.resolveInitialization) {
      this.resolveInitialization();
      this.resolveInitialization = null;
    }
  }

  private async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.instance(config);
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          // Server responded with error status
          const serverResponse = error.response.data as ApiResponse<T>;

          if (serverResponse && !serverResponse.success) {
            return {
              ...serverResponse,
              error:
                serverResponse.error ||
                `HTTP ${error.response.status}: ${error.response.statusText}`,
              message: serverResponse.message || serverResponse.error || 'Server error occurred',
            };
          }

          return serverResponse;
        } else if (error.request) {
          // Request was made but no response received
          let errorMessage = 'Cannot connect to backend server';

          if (error.code === 'ECONNREFUSED') {
            errorMessage = 'Backend server is not running or unreachable';
          } else if (error.code === 'ENOTFOUND') {
            errorMessage = 'Cannot resolve backend server address';
          } else if (error.code === 'ECONNABORTED') {
            errorMessage = 'Request timeout - backend server is not responding';
          } else if (error.code === 'ERR_NETWORK') {
            errorMessage = 'Network error - check your internet connection';
          }

          return {
            success: false,
            error: 'Connection Error',
            message: errorMessage,
            timestamp: new Date().toISOString(),
          };
        } else {
          // Something else happened in setting up the request
          return {
            success: false,
            error: 'Request Error',
            message: error.message || 'Failed to make request to backend',
            timestamp: new Date().toISOString(),
          };
        }
      }

      // Non-axios errors
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      return {
        success: false,
        error: 'Client Error',
        message: errorMessage,
        timestamp: new Date().toISOString(),
      };
    }
  }

  // Health endpoints
  async healthCheck(): Promise<ApiResponse<{ status: string; message: string }>> {
    return this.request({
      method: 'GET',
      url: '/test',
    });
  }

  // Authentication endpoints
  async getIntervalsAuthUrl(): Promise<ApiResponse<AuthUrlResponse>> {
    return this.request({
      method: 'GET',
      url: '/api/auth/intervals/login',
    });
  }

  async handleIntervalsCallback(
    code: string,
    state: string
  ): Promise<ApiResponse<IntervalsTokenData>> {
    return this.request({
      method: 'GET',
      url: '/api/auth/intervals/callback',
      params: { code, state },
    });
  }

  // Training endpoints
  async generateTrainingPlan(
    userProfile: UserProfile,
    startDate?: string
  ): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'POST',
      url: '/api/training/generate-plan',
      data: { ...userProfile, startDate },
    });
  }

  async skipAhead(weeksToSkip: number): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'POST',
      url: '/api/training/skip-ahead',
      data: { weeksToSkip },
    });
  }

  async generateOrUpdateTrainingPlan(
    userProfile: UserProfile,
    existingPlan?: TrainingPlan
  ): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'POST',
      url: '/api/training/generate-or-update-plan',
      data: {
        userProfile,
        existingPlan: existingPlan || null,
      },
    });
  }

  async getDeloadWeek(): Promise<ApiResponse<{ deloadWeek: number }>> {
    return this.request({
      method: 'GET',
      url: '/api/training/deload-week',
    });
  }

  async setDeloadWeek(userProfile: UserProfile, week: number): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'PUT',
      url: '/api/training/deload-week',
      data: { week, userProfile },
    });
  }

  async deleteWorkout(
    userProfile: UserProfile,
    trainingPlan: TrainingPlan,
    workoutIndex: number
  ): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'DELETE',
      url: '/api/training/workout',
      data: {
        userProfile,
        trainingPlan,
        workoutIndex,
      },
    });
  }

  async getWeeklyPreview(params: {
    userProfile: UserProfile;
    weekNumber: number;
    weekType: string;
    isDeloadWeek: boolean;
    plannedWeeklyVolume: number;
    longRunDistance: number;
    startDate: string;
  }): Promise<ApiResponse> {
    return this.request({
      method: 'POST',
      url: '/api/training/weekly-preview',
      data: params,
    });
  }

  async getDailyWorkout(params: {
    userProfile: UserProfile;
    dayOfWeek: string;
    weekNumber: number;
    weekType: string;
    isDeloadWeek: boolean;
    plannedWeeklyVolume: number;
    longRunDistance: number;
    workoutDate: string;
    currentWeekRunningSessions?: number;
    currentWeekCyclingSessions?: number;
    totalSessionsThisWeek?: number;
    daysSinceLastBenchmark?: number;
  }): Promise<ApiResponse> {
    return this.request({
      method: 'POST',
      url: '/api/training/daily-workout',
      data: params,
    });
  }

  async swapWorkouts(
    userProfile: UserProfile,
    trainingPlan: TrainingPlan,
    workoutIndex1: number,
    workoutIndex2: number
  ): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'POST',
      url: '/api/training/swap-workouts',
      data: {
        userProfile,
        trainingPlan,
        workoutIndex1,
        workoutIndex2,
      },
    });
  }

  async updateLongRunDistance(
    userProfile: UserProfile,
    trainingPlan: TrainingPlan,
    workoutIndex: number,
    newDistance: number
  ): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'PUT',
      url: '/api/training/long-run-distance',
      data: {
        userProfile,
        trainingPlan,
        workoutIndex,
        newDistance,
      },
    });
  }

  async getWorkoutTypes(): Promise<
    ApiResponse<Array<{ id: string; name: string; type: string; sportType: 'running' | 'cycling' }>>
  > {
    return this.request({
      method: 'GET',
      url: '/api/training/workout-types',
    });
  }

  async getWorkoutTypesFull(): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        type: string;
        sportType: 'running' | 'cycling';
        minTime: number;
        load: number;
        TSS?: number;
        environment?: string;
        steps: WorkoutStep[];
      }>
    >
  > {
    return this.request({
      method: 'GET',
      url: '/api/training/workout-types?full=true',
    });
  }

  /**
   * Get a single bike workout by ID
   */
  async getBikeWorkout(id: string): Promise<
    ApiResponse<{
      id: string;
      name: string;
      type: 'easy' | 'hard';
      sportType: 'cycling';
      category?: string;
      minTime: number;
      durationSeconds?: number;
      load: number;
      TSS?: number;
      total_distance?: number;
      steps: WorkoutStep[];
      description?: string;
      source?: string;
      sourceUrl?: string;
      popularity?: number;
    }>
  > {
    return this.request({
      method: 'GET',
      url: `/api/training/bike-workouts/${encodeURIComponent(id)}`,
    });
  }

  /**
   * Search bike workouts from library with filters and pagination
   */
  async searchBikeWorkouts(params: {
    skip?: number;
    limit?: number;
    name?: string;
    category?: string;
    durationMin?: number;
    durationMax?: number;
    loadMin?: number;
    loadMax?: number;
    favoritesOnly?: boolean;
  }): Promise<
    ApiResponse<{
      workouts: Array<{
        id: string;
        name: string;
        type: 'easy' | 'hard';
        sportType: 'cycling';
        category?: string;
        minTime: number;
        durationSeconds?: number;
        load: number;
        total_distance?: number;
        steps: WorkoutStep[];
        description?: string;
        source?: string;
        sourceUrl?: string;
        popularity?: number;
      }>;
      total: number;
      skip: number;
      limit: number;
      categories: string[];
    }>
  > {
    const queryParams = new URLSearchParams();

    if (params.skip !== undefined) queryParams.set('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.set('limit', params.limit.toString());
    if (params.name) queryParams.set('name', params.name);
    if (params.category) queryParams.set('category', params.category);
    if (params.durationMin !== undefined)
      queryParams.set('durationMin', params.durationMin.toString());
    if (params.durationMax !== undefined)
      queryParams.set('durationMax', params.durationMax.toString());
    if (params.loadMin !== undefined) queryParams.set('loadMin', params.loadMin.toString());
    if (params.loadMax !== undefined) queryParams.set('loadMax', params.loadMax.toString());
    if (params.favoritesOnly) queryParams.set('favoritesOnly', 'true');

    return this.request({
      method: 'GET',
      url: `/api/training/bike-workouts?${queryParams.toString()}`,
    });
  }

  async previewWorkoutChange(
    workoutIndex: number,
    newWorkoutTypeId: string
  ): Promise<
    ApiResponse<{
      preview: {
        changedWorkout: { before: Workout; after: Workout };
        affectedWorkouts: Array<{ before: Workout; after: Workout; reason: string }>;
        warnings: string[];
        isValid: boolean;
      };
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/training/preview-workout-change',
      data: {
        workoutIndex,
        newWorkoutTypeId,
      },
    });
  }

  async applyWorkoutChange(
    workoutIndex: number,
    newWorkoutTypeId: string
  ): Promise<
    ApiResponse<{
      trainingPlan: TrainingPlan;
      preview: {
        changedWorkout: { before: Workout; after: Workout };
        affectedWorkouts: Array<{ before: Workout; after: Workout; reason: string }>;
        warnings: string[];
        isValid: boolean;
      };
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/training/apply-workout-change',
      data: {
        workoutIndex,
        newWorkoutTypeId,
      },
    });
  }

  // Profile endpoints (token-based authentication)
  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    return this.request({
      method: 'GET',
      url: '/api/profile',
    });
  }

  async updateUserProfile(profile: UserProfile): Promise<ApiResponse<UserProfile>> {
    return this.request({
      method: 'PUT',
      url: '/api/profile',
      data: profile,
    });
  }

  async toggleFavoriteWorkout(
    workoutId: string
  ): Promise<ApiResponse<{ isFavorite: boolean; favoriteWorkouts: string[] }>> {
    return this.request({
      method: 'POST',
      url: `/api/profile/favorites/${workoutId}`,
    });
  }

  // Goal Agent endpoints
  async getGoalSuggestions(
    goal?: string,
    date?: string
  ): Promise<ApiResponse<GoalSuggestionResponse>> {
    // AI agent calls can take 60+ seconds due to thread creation, polling, etc.
    return this.request({
      method: 'POST',
      url: '/api/ai-agents/goal-agent/suggest',
      data: { goal, date },
      timeout: 120000, // 2 minute timeout for AI agent calls
    });
  }

  async saveTrainingGoal(goal: TrainingGoal): Promise<ApiResponse<TrainingGoal>> {
    return this.request({
      method: 'POST',
      url: '/api/ai-agents/goal-agent/save',
      data: goal,
    });
  }

  async clearTrainingGoal(): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: '/api/ai-agents/goal-agent/clear',
    });
  }

  async updateGoalLikelihood(): Promise<ApiResponse<LikelihoodUpdateResponse>> {
    // AI agent calls can take 60+ seconds
    return this.request({
      method: 'POST',
      url: '/api/ai-agents/goal-agent/update-likelihood',
      timeout: 120000, // 2 minute timeout for AI agent calls
    });
  }

  // Training plan storage endpoints
  async getStoredTrainingPlan(): Promise<ApiResponse<TrainingPlan>> {
    return this.request({
      method: 'GET',
      url: '/api/training/plan',
    });
  }

  async updateStoredTrainingPlan(trainingPlan: TrainingPlan): Promise<
    ApiResponse<{
      trainingPlan: TrainingPlan;
      intervalsSync: {
        synced: boolean;
        successfulSyncs?: number;
        failedSyncs?: number;
        message?: string;
      };
    }>
  > {
    return this.request({
      method: 'PUT',
      url: '/api/training/plan',
      data: trainingPlan,
    });
  }

  async scheduleWorkout(
    date: string,
    workoutId: string
  ): Promise<ApiResponse<{ trainingPlan: TrainingPlan; scheduledWorkout: Workout }>> {
    return this.request({
      method: 'POST',
      url: '/api/training/schedule-workout',
      data: { date, workoutId },
    });
  }

  // Intervals.icu endpoints
  async linkIntervalsAccount(): Promise<
    ApiResponse<{ authUrl: string; state: string; hasValidToken: boolean }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/intervals/link',
    });
  }

  async unlinkIntervalsAccount(): Promise<ApiResponse<{ unlinkedAt: string }>> {
    return this.request({
      method: 'POST',
      url: '/api/intervals/unlink',
    });
  }

  async getIntervalsHealth(): Promise<ApiResponse<HealthData>> {
    return this.request({
      method: 'GET',
      url: '/api/intervals/health',
    });
  }

  async getIntervalsStatus(): Promise<
    ApiResponse<{
      connected: boolean;
      message?: string;
      athlete?: { id: string | null; name: string | null; email: string | null };
      healthData?: HealthData;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/api/intervals/status',
    });
  }

  async testIntervalsApi(): Promise<
    ApiResponse<{ status: string; tests: Record<string, boolean> }>
  > {
    return this.request({
      method: 'GET',
      url: '/api/intervals/test',
    });
  }

  // Intervals.icu sync endpoints
  async syncWorkoutToIntervals(
    workout: Workout
  ): Promise<ApiResponse<{ eventId: string; syncedAt: string }>> {
    return this.request({
      method: 'POST',
      url: '/api/intervals/sync/workout',
      data: workout,
    });
  }

  async syncTrainingPlanToIntervals(trainingPlan: TrainingPlan): Promise<
    ApiResponse<{
      totalWorkouts: number;
      successfulSyncs: number;
      failedSyncs: number;
      results: Array<{
        workout: string;
        date: string;
        success: boolean;
        eventId?: string;
        error?: string;
      }>;
      syncedAt: string;
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/intervals/sync/training-plan',
      data: trainingPlan,
    });
  }

  async clearIntervalsEvents(): Promise<ApiResponse<{ clearedAt: string }>> {
    return this.request({
      method: 'DELETE',
      url: '/api/intervals/sync/clear',
    });
  }

  async getFitnessHistory(days: number = 90): Promise<
    ApiResponse<{
      history: Array<{
        date: string;
        atl: number;
        ctl: number;
        tsb: number;
      }>;
      days: number;
      count: number;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/api/intervals/fitness-history',
      params: { days },
    });
  }

  async syncFitnessData(force: boolean = false): Promise<
    ApiResponse<{
      connected: boolean;
      fitnessData: {
        atl: number;
        ctl: number;
        tsb: number;
        dailyTss: number;
        lastSyncedAt: string;
        syncSource: 'intervals.icu';
      } | null;
      wasStale?: boolean;
      syncedFromCache?: boolean;
      message?: string;
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/intervals/sync-fitness',
      data: { force },
    });
  }

  // Health Status endpoints
  async getHealthStatus(): Promise<
    ApiResponse<{
      healthStatus: 'normal' | 'sick' | 'injured';
      statusHistory: Array<{
        status: 'normal' | 'sick' | 'injured';
        startDate: string;
        endDate: string | null;
        durationDays?: number;
      }>;
    }>
  > {
    return this.request({
      method: 'GET',
      url: '/api/status/user-status',
    });
  }

  async updateHealthStatus(
    status: 'normal' | 'sick' | 'injured',
    date?: string
  ): Promise<
    ApiResponse<{
      healthStatus: 'normal' | 'sick' | 'injured';
      statusHistory: Array<{
        status: 'normal' | 'sick' | 'injured';
        startDate: string;
        endDate: string | null;
        durationDays?: number;
      }>;
      adjustmentInfo?: {
        sickDurationDays?: number;
        adjustmentType?: 'short' | 'long' | 'none';
        daysAffected?: number;
        workoutsCancelled?: number;
        currentDayCancelled?: boolean;
      };
    }>
  > {
    return this.request({
      method: 'POST',
      url: '/api/status/user-status',
      data: { status, ...(date && { date }) },
    });
  }

  // AI Coach endpoints
  async sendAICoachMessage(prompt: string): Promise<ApiResponse<AICoachChatResponse>> {
    return this.request({
      method: 'POST',
      url: '/api/ai-agents/agent/chat',
      data: { prompt },
      timeout: 60000, // AI agent may take longer to respond
    });
  }

  async applyAICoachChanges(
    changes: AgentSessionChange[]
  ): Promise<ApiResponse<AICoachApplyResponse>> {
    return this.request({
      method: 'POST',
      url: '/api/ai-agents/agent/apply-changes',
      data: { changes },
    });
  }

  // Generic HTTP methods for services that need them
  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    return this.request({
      method: 'GET',
      url,
      params,
    });
  }

  async post<T>(url: string, data?: any, options?: { timeout?: number }): Promise<ApiResponse<T>> {
    return this.request({
      method: 'POST',
      url,
      data,
      ...(options?.timeout && { timeout: options.timeout }),
    });
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return this.request({
      method: 'PUT',
      url,
      data,
    });
  }

  async delete<T = void>(url: string): Promise<ApiResponse<T>> {
    return this.request({
      method: 'DELETE',
      url,
    });
  }
}

// Create and export the API client instance
const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE,
  timeout: 15000,
});

export default apiClient;
